<template>
  <div class="view-player">
    <dmb-show v-if="show" :orientation="orientation" :show="show"
              :sync-anchor-at="syncAnchorMs" :server-now="serverNowFn"/>

    <dmb-show-message v-else-if="error" retry-button @retry="init">{{ error }}</dmb-show-message>
    <dmb-show-message v-else-if="loading">Presentatie laden...</dmb-show-message>
    <dmb-show-message v-else>Geen presentatie om weer te geven</dmb-show-message>
  </div>
</template>

<script>

import {Options, Vue} from 'vue-class-component'
import {cloneDeep, compact, first, get, uniq} from 'lodash';

import StorageKey from '@/js/enum/StorageKey';
import Init from '@/gql/query/Init.gql';
import FindPresentation from '@/gql/query/FindPresentation.gql';

import {PlayerStoreAction} from '@/js/store/player';

import ShowMessage from 'common/js/component/ShowMessage.vue';
import playerMixin from 'common/js/mixin/player';
import {getAssetsFromShow} from 'common/js/util/asset';
import {generateCustomFontFaces, generateGoogleFontsUrl, getFontsFromShow} from 'common/js/util/font';
import {preloadAssets} from 'common/js/util/preload';
import RealtimeSubject from 'common/js/enum/RealtimeSubject';
import {StyleStoreMutation} from 'common/js/store/style';
import RealtimeOperation from 'common/js/enum/RealtimeOperation';
import {AssetStoreAction, AssetStoreMutation} from 'common/js/store/assets';
import ApiErrorType from 'common/js/enum/ApiErrorType';

export default Options({
  name: 'Player',
  mixins: [playerMixin],
  components: {
    [ShowMessage.name]: ShowMessage
  }
})(
    class Player extends Vue {
      loading = 0;
      error = null;
      updating = false;

      screen = null;
      show = null;

      // Active campaigns targeting this screen, keyed by campaignId.
      // ACTIVATE adds an entry, DELETE removes one. Their slides are all
      // appended to the base allocation's playlist by determineShow().
      campaignShows = {};

      // Latest sync anchor (server-time ms). Initially populated from
      // findSystemScreenByCode.system.syncAnchorAt, then mutated when
      // SYSTEM:ACTIVATE/UPDATE or CAMPAIGN:ACTIVATE/DELETE payloads land
      // carrying a fresh anchor. Null on non-sync screens — <dmb-show>
      // falls through to its existing local-timer path in that case.
      syncAnchorMs = null;

      get orientation() {
        return this.screen?.system.orientation;
      }

      get syncEnabled() {
        return !!this.screen?.system?.synchronize;
      }

      // Bound serverNow passed to <dmb-show>; null when sync is off so the
      // child's syncAnchorAt-and-serverNow gate stays false and the original
      // setTimeout chain runs. Computed (not method) so Vue caches the
      // function reference across renders and <dmb-show> doesn't reinit.
      get serverNowFn() {
        if (!this.syncEnabled) return null;
        return this.$di.realtime.serverNow.bind(this.$di.realtime);
      }

      get code() {
        return window.localStorage.getItem(StorageKey.SCREEN_CODE);
      }

      mounted() {
        if (!this.code) {
          this.$router.replace({name: 'login'});
        }

        this.init();
      }

      unmounted() {

        this.stopUpdating();
      }

      async init() {
        this.logger.info('Initializing...');

        this.error = null;
        this.loading++;

        try {
          await this.populatePlayerStores();

          // TODO: uncomment when font uploader is added again
          this.loadCustomFonts();
          await this.updateScreen();

          this.logger.info('Initialize completed');

          // Update Cycle
          if (!this.updating) {
            this.startUpdating();
            this.$di.realtime.connect(`SCREEN:${this.code}`);
          }
        } catch (e) {

          console.error(e);

          const apiErrors = e.networkError?.result?.errors;
          const errorType = apiErrors?.length ? apiErrors[0].type : null;

          if (errorType === ApiErrorType.ORGANIZATION_INACTIVE) {
            this.error = 'Systeem inactief. Neem contact op met DigiMenuBox.';
          } else {
            this.error = 'De presentatie kan niet worden geladen, controleer de internetverbinding';
          }
        }

        this.loading--;
      }

      async updateScreen() {
        const response = await this.$apollo.query({
          fetchPolicy: 'network-only',
          query: Init,
          variables: {
            code: this.code
          }
        });

        const screen = get(response, 'data.findSystemScreenByCode');
        if (!screen) {
          throw 'Invalid response';
        }

        this.screen = cloneDeep(screen);

        // Pick up the initial anchor (may be null if sync just turned on
        // and no content has activated yet) and gate the recalibration loop
        // on whether this System actually uses sync.
        this.updateAnchor(screen.system.syncAnchorAt);
        if (screen.system.synchronize) {
          this.$di.realtime.startPeriodicClockSync?.();
        } else {
          this.$di.realtime.stopPeriodicClockSync?.();
        }

        this.logger.info(`- Loaded screen ${screen.sortIndex} in system "${screen.system.title}"`);

        this.$store.commit(AssetStoreMutation.SET_LOGO_URL, screen.system.organization.logoUrl);
        await this.loadShows();
        await this.loadActiveCampaigns();

        // Determine Show
        const show = this.determineShow();
        await this.updateShow(show);
      }

      async loadActiveCampaigns() {

        const active = this.screen.activeCampaigns || [];
        const wanted = new Set();

        for (const campaign of active) {

          const campaignId = String(campaign.id);
          wanted.add(campaignId);

          if (this.campaignShows[campaignId]) {
            continue;
          }

          await this.loadCampaign(campaignId, campaign.presentationId);
        }

        // Drop any cached entries that the server no longer considers active
        // (e.g. expired while the page was reloading).
        const next = {};
        let changed = false;
        for (const [id, show] of Object.entries(this.campaignShows)) {
          if (wanted.has(id)) {
            next[id] = show;
          } else {
            changed = true;
          }
        }
        if (changed) {
          this.campaignShows = next;
        }
      }

      async loadCampaign(campaignId, presentationId) {

        this.logger.info('Loading active campaign', campaignId, 'presentation', presentationId);

        const response = await this.$apollo.query({
          query: FindPresentation,
          variables: { id: presentationId },
          fetchPolicy: 'network-only'
        });

        const presentation = get(response, 'data.findPresentation');
        const showId = presentation?.publishedShow?.id;
        if (!showId) {
          this.logger.warn('Campaign presentation has no published show', presentationId);
          return;
        }

        await this.$store.dispatch(PlayerStoreAction.FETCH_SHOW, showId);
        const campaignShow = this.$store.state.player.showsById[showId];

        this.campaignShows = { ...this.campaignShows, [campaignId]: campaignShow };
      }

      async updateShow(show) {
        if (!show) {

          this.logger.info('Clearing show');
          this.show = null;
        } else if (show.id !== this.show?.id) {
          // Preload Fonts
          this.logger.info(`- Preloading fonts...`);

          this.loadGoogleFonts(getFontsFromShow(show, show.slides));
          // TODO: uncomment when font uploader is added again
          this.loadCustomFonts();

          // Preload Assets
          this.logger.info(`- Preloading assets... 0%`);

          await preloadAssets(
              getAssetsFromShow(show, this.$store.getters.getAsset), (progress) => {
                this.logger.info(`- Preloading assets... ${progress}%`);
              }
          );

          // Switch to show
          this.logger.info(`Switching to show ${show.id}`, show);

          this.$store.commit(StyleStoreMutation.SET_SHOW_STYLE, show.style);
          this.show = show;
        }
      }

      loadGoogleFonts(fonts) {
        if (this.googleFontsLink) {

          document.head.removeChild(this.googleFontsLink);
          this.googleFontsLink = null;
        }

        if (fonts.length) {

          const googleFontsLink = document.createElement('link');
          googleFontsLink.type = 'text/css';
          googleFontsLink.rel = 'stylesheet';
          googleFontsLink.href = generateGoogleFontsUrl(fonts);

          document.head.appendChild(googleFontsLink);
          this.googleFontsLink = googleFontsLink;
        }
      }

      loadCustomFonts() {
        generateCustomFontFaces(this.$store.state.assets.assets);
      }

      async loadShows() {
        // Load shows
        const showIds = uniq(compact(
            this.screen.allocations.map(allocation => allocation.presentation.publishedShow?.id)
        ));

        this.logger.info(`- Fetching ${showIds.length} shows...`);

        for (let showId of showIds) {

          this.logger.info(`-- Fetching show ${showId}`);
          await this.$store.dispatch(PlayerStoreAction.FETCH_SHOW, showId);
        }
      }

      async reloadPresentation(presentationId) {
        this.logger.info('Replacing presentation', presentationId);

        const response = await this.$apollo.query({
          query: FindPresentation,
          variables: {
            id: presentationId
          },
          fetchPolicy: 'network-only'
        });

        const presentation = get(response, 'data.findPresentation');
        if (!presentation) {
          throw 'Invalid response';
        }

        // Replace presentation
        for (let allocation of this.screen.allocations) {

          if (allocation.presentation.id === presentation.id) {
            allocation.presentation = presentation;
          }
        }

        return presentation;
      }

      determineShow() {

        const baseShow = this.determineBaseShow();

        // Stable ordering of concurrent campaigns by campaignId so the
        // playlist composition is deterministic.
        const campaignEntries = Object.entries(this.campaignShows)
            .sort(([a], [b]) => Number(a) - Number(b))
            .map(([, show]) => show)
            .filter(Boolean);

        // No campaigns -> play the base allocation as-is.
        if (campaignEntries.length === 0) {
          return baseShow;
        }

        // Sort each show's own slides by sortIndex, then re-stamp sortIndex
        // on a fresh copy so the composite plays base-first-then-each-
        // campaign-in-order. dmb-show re-sorts by sortIndex internally,
        // so without re-stamping the base/campaign slides interleave
        // (both start at sortIndex 0).
        const sortedSegments = [
          baseShow?.slides ?? [],
          ...campaignEntries.map(s => s.slides ?? [])
        ].map(slides => [...slides].sort((a, b) => a.sortIndex - b.sortIndex));

        let offset = 0;
        const slides = sortedSegments.flatMap(segment => {
          const stamped = segment.map((slide, i) => ({ ...slide, sortIndex: offset + i }));
          offset += segment.length;
          return stamped;
        });

        const compositeId = (baseShow ? baseShow.id + '+' : '') +
            campaignEntries.map(s => s.id).join('+');

        if (!baseShow) {
          return {
            ...campaignEntries[0],
            id: compositeId,
            slides
          };
        }

        return {
          ...baseShow,
          id: compositeId,
          slides
        };
      }

      determineBaseShow() {
        const allocations = this.screen.allocations;
        let activeAllocation = first(allocations.filter(allocation => !allocation.collection.startTime));

        if (this.screen.system.activeCollectionId) {

          const systemAllocation = allocations.find(allocation => allocation.collection.id === this.screen.system.activeCollectionId);
          activeAllocation = systemAllocation || null;
        }

        if (!activeAllocation || !activeAllocation.presentation.publishedShow) {
          return null;
        }

        const showId = activeAllocation.presentation.publishedShow.id;
        return this.$store.state.player.showsById[showId];
      }

      startUpdating() {
        this.startUpdatingFeeds();

        const realtime = this.$di.realtime;

        realtime.handler(RealtimeSubject.PRESENTATION, this.handlePresentationOperation.bind(this));
        realtime.handler(RealtimeSubject.SYSTEM, this.handleSystemOperation.bind(this));
        realtime.handler(RealtimeSubject.COLLECTION, this.handleCollectionOperation.bind(this));
        realtime.handler(RealtimeSubject.CAMPAIGN, this.handleCampaignOperation.bind(this));

        realtime.onReconnect(attempts => {

          setTimeout(async () => {

            if (attempts > 50) {

              document.location.reload();
            } else if (attempts > 10) {

              this.logger.info('Refetching stores & screen because of previous connection failure');

              // Refetch everything
              await this.populatePlayerStores();
              await this.updateScreen();
            }

          }, 10 * 1000);
        });
      }

      stopUpdating() {
        this.stopUpdatingFeeds();

        const realtime = this.$di.realtime;

        realtime.removeHandler(RealtimeSubject.PRESENTATION);
        realtime.removeHandler(RealtimeSubject.SYSTEM);
        realtime.removeHandler(RealtimeSubject.COLLECTION);
        realtime.removeHandler(RealtimeSubject.CAMPAIGN);
        realtime.stopPeriodicClockSync?.();
      }

      async handleCampaignOperation({ data, operation }) {

        const campaignId = data?.campaignId?.toString();
        const presentationId = data?.presentationId;

        // Anchor before any show swap so <dmb-show> sees the new anchor
        // and the new show in the same reactivity tick.
        if (data?.syncAnchorAt != null) {
          this.updateAnchor(data.syncAnchorAt);
        }

        if (!campaignId) {
          return;
        }

        if (operation === RealtimeOperation.ACTIVATE) {

          if (!presentationId) {
            return;
          }
          await this.activateCampaign(campaignId, presentationId);
        }
        else if (operation === RealtimeOperation.UPDATE) {

          // Only re-fetch if this campaign is currently active for us.
          if (this.campaignShows[campaignId] && presentationId) {
            await this.activateCampaign(campaignId, presentationId);
          }
        }
        else if (operation === RealtimeOperation.DELETE) {

          if (!this.campaignShows[campaignId]) {
            return;
          }

          const { [campaignId]: _removed, ...rest } = this.campaignShows;
          this.campaignShows = rest;

          await this.updateShow(this.determineShow());
        }
      }

      async activateCampaign(campaignId, presentationId) {

        await this.loadCampaign(campaignId, presentationId);
        await this.updateShow(this.determineShow());
      }

      async handlePresentationOperation({data}) {
        await this.$store.dispatch(AssetStoreAction.LOAD_ASSETS);

        // Presentation changed, reload show
        if (this.show && data.presentationId.toString() === this.show.presentationId.toString()) {
          const presentation = await this.reloadPresentation(data.presentationId);
          const newShowId = presentation.publishedShow?.id;

          if (newShowId) {
            await this.$store.dispatch(PlayerStoreAction.FETCH_SHOW, newShowId);

            // If any active campaign uses this presentation, refresh that
            // entry too — otherwise determineShow() keeps a stale snapshot.
            const next = { ...this.campaignShows };
            let changed = false;
            for (const [campaignId, show] of Object.entries(next)) {
              if (show?.presentationId?.toString() === data.presentationId.toString()) {
                next[campaignId] = this.$store.state.player.showsById[newShowId];
                changed = true;
              }
            }
            if (changed) {
              this.campaignShows = next;
            }

            const show = this.determineShow();
            await this.updateShow(show);
          } else {
            await this.updateShow(null);
          }
        }
      }

      async handleSystemOperation({data, operation}) {

        // Anchor before any state mutation so <dmb-show> picks it up
        // alongside the show prop change in a single reactivity tick.
        if (data?.syncAnchorAt != null) {
          this.updateAnchor(data.syncAnchorAt);
        }

        if (operation === RealtimeOperation.ACTIVATE) {

          this.screen.system.activeCollectionId = data.collectionId.toString();

          const show = this.determineShow();
          await this.updateShow(show);
        } else if (operation === RealtimeOperation.UPDATE) {

          // Pure anchor refresh — no content actually changed (e.g. the
          // dashboard just toggled `synchronize` on, or resetSystemSyncAnchor
          // ran for some other trigger). Avoid the full updateScreen()
          // refetch, which would tear down the active show.
          const payloadKeys = Object.keys(data || {});
          const onlyAnchor = payloadKeys.every(k => k === 'syncAnchorAt' || k === 'systemId');
          if (data?.syncAnchorAt != null && onlyAnchor) {
            return;
          }

          this.updateScreen();
        }
      }

      async handleCollectionOperation() {
        this.updateScreen();
      }

      updateAnchor(value) {
        if (value == null) {
          this.syncAnchorMs = null;
          return;
        }
        // Server sends either an ms number (per-op broadcasts) or an
        // ISO/SQL datetime string (the GraphQL Init query). Normalize both
        // to ms so <dmb-show> always sees a Number.
        const ms = typeof value === 'number' ? value : new Date(value).getTime();
        if (!isNaN(ms)) {
          this.syncAnchorMs = ms;
        }
      }
    }
)

</script>
<style lang="scss" src="../../../scss/route/Player.scss"></style>
